import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Space, message, Modal, Form, 
  Input, Select, Popconfirm, Tooltip 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getQuestions, addQuestion, updateQuestion, deleteQuestion } from '../../api/admin';

const { TextArea } = Input;
const { Option } = Select;

const QuestionManage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await getQuestions();
      if (response.code === 200) {
        setQuestions(response.data.questions);
      }
    } catch (error) {
      message.error('加载题库失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingQuestion(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingQuestion(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteQuestion(id);
      if (response.code === 200) {
        message.success('删除成功');
        loadQuestions();
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingQuestion) {
        const response = await updateQuestion(editingQuestion.id, values);
        if (response.code === 200) {
          message.success('更新成功');
        }
      } else {
        const response = await addQuestion(values);
        if (response.code === 200) {
          message.success('添加成功');
        }
      }
      
      setModalVisible(false);
      loadQuestions();
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '准则序号',
      dataIndex: 'item_number',
      key: 'item_number',
      width: 100
    },
    {
      title: '题目文本',
      dataIndex: 'question_text',
      key: 'question_text',
      ellipsis: true
    },
    {
      title: '正确答案',
      dataIndex: 'correct_option',
      key: 'correct_option',
      width: 100
    },
    {
      title: '错误类型',
      dataIndex: 'error_type',
      key: 'error_type',
      width: 150
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              size="small"
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除此题目?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="删除">
              <Button 
                type="link" 
                size="small"
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card>
        <h2 style={{ marginBottom: 16 }}>找茬题库</h2>
        
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          style={{ marginBottom: 16 }}
        >
          添加题目
        </Button>

        <Table
          columns={columns}
          dataSource={questions}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingQuestion ? '编辑题目' : '添加题目'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="item_number"
            label="所属准则"
            rules={[{ required: true, message: '请选择准则' }]}
          >
            <Select>
              <Option value={1}>准则1: 先执行再优化</Option>
              <Option value={2}>准则2: 守时守信守规矩</Option>
              <Option value={3}>准则3: 沟通坦诚讲真话</Option>
              <Option value={4}>准则4: 主动担当不甩锅</Option>
              <Option value={5}>准则5: 以身作则带队伍</Option>
              <Option value={6}>准则6: 目标导向重实绩</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="question_text"
            label="题目文本(错误版本)"
            rules={[{ required: true, message: '请输入题目文本' }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="option_a"
            label="选项A"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="option_b"
            label="选项B"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="option_c"
            label="选项C"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="option_d"
            label="选项D"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="correct_option"
            label="正确答案"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="A">A</Option>
              <Option value="B">B</Option>
              <Option value="C">C</Option>
              <Option value="D">D</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="error_type"
            label="错误类型"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="关键词替换">关键词替换</Option>
              <Option value="删除修饰语">删除修饰语</Option>
              <Option value="逻辑错误">逻辑错误</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="explanation"
            label="题目说明"
          >
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default QuestionManage;
